"use client";

/**
 * RequestStep — forgot-password step 1: email input + Send OTP button.
 */

import type React from "react";
import { useCallback, useState } from "react";
import CButton from "@/Shared/CButton/CButton";
import CInput from "@/Shared/CInput/CInput";
import isValidEmail, { INVALID_EMAIL_MESSAGE } from "@/utils/isValidEmail";

export interface RequestStepProps {
  email: string;
  setEmail: (v: string) => void;
  onRequest: () => void;
  loading?: boolean;
}

const REQUEST_FIELD_CLASS =
  "h-auto py-3 bg-primary-50/40 border-2 border-transparent shadow-none focus-visible:border-primary-600 focus-visible:ring-primary-600/20";

/**
 * Collects the user's email and triggers the OTP request when valid.
 */
const RequestStep = ({
  email,
  setEmail,
  onRequest,
  loading = false,
}: RequestStepProps) => {
  const [emailError, setEmailError] = useState<string | null>(null);

  const validateEmail = useCallback((value: string): boolean => {
    const trimmed = value.trim();
    // Mirror loginAction — reject malformed addresses before OTP request.
    if (!isValidEmail(trimmed)) {
      setEmailError(INVALID_EMAIL_MESSAGE);
      return false;
    }
    setEmailError(null);
    return true;
  }, []);

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateEmail(email)) return;
    onRequest();
  };

  return (
    <form onSubmit={onSubmit} noValidate className="space-y-4">
      <CInput
        id="forgot-email"
        label="Email"
        type="email"
        value={email}
        onChange={(e) => {
          setEmailError(null);
          setEmail(e.target.value);
        }}
        onBlur={() => {
          // Validate on blur when the user has typed something.
          if (email.trim()) validateEmail(email);
        }}
        placeholder="Enter your email"
        autoComplete="email"
        required
        disabled={loading}
        error={emailError ?? undefined}
        className={REQUEST_FIELD_CLASS}
      />

      <CButton
        type="submit"
        size="lg"
        className="w-full"
        disabled={loading || !email.trim()}
      >
        {loading ? "Sending..." : "Send OTP"}
      </CButton>
    </form>
  );
};

export default RequestStep;
export { RequestStep };
